import type { SalaryRoundingMode, SalarySource } from './salaryDefinition';

/**
 * §4.5's target band for a draftable pool, and the reason it is a band rather than a number.
 *
 * Simulated over the reference data, varying only the qualification rule: at 941 draftable
 * players 45% of entrants score zero and the median score is 10; at 127 that is 16% and 38; at 16
 * every team is nearly identical. Lives here rather than in the validator so the rule and the
 * sentence that explains it cannot part company.
 */
export const TARGET_POOL_MINIMUM = 100;
export const TARGET_POOL_MAXIMUM = 200;

/**
 * Every sentence the salary model builder says, in one file.
 *
 * <b>The reader runs a card room</b> (§9.3). Two habits follow, and both are rules rather than
 * taste:
 *
 * - **Never show a raw percentile.** "Sits at the 86.4th percentile of the composite" is a true
 *   sentence that answers nothing an operator asked. What they want to know is *which players
 *   these are* and *what they cost*, so a band is described by the share of the pool it covers and
 *   by how many players actually landed in it, and a player is described by what they did.
 * - **Every claim about the model is a claim about their own figures.** The worked examples under
 *   the controls are computed from the definition on screen, not written as prose about a
 *   hypothetical one.
 *
 * Kept in one file for the same reason `points/wording.ts` is: an option has to read the same
 * wherever it appears, and "does this screen talk like the rest of the product" is a question
 * somebody should be able to answer by reading one file.
 */

export interface Choice<T extends string> {
  value: T;
  label: string;
  detail: string;
}

/**
 * §4.2's six inputs, in the order the reference model weights them — frequency first, money last,
 * which is the whole argument of §4.3 expressed as an ordering.
 */
export const SALARY_SOURCE_CHOICES: readonly Choice<SalarySource>[] = [
  {
    value: 'cashCount',
    label: 'How often they cash',
    detail:
      'The strongest signal there is. Over the reference series, somebody who cashed twice was more than twice as likely to cash again as somebody who cashed once.',
  },
  {
    value: 'leaderboardPoints',
    label: 'Leaderboard points',
    detail:
      'What your points system already worked out, so a deep run counts for more than a min-cash. Reads from the leaderboards below.',
  },
  {
    value: 'totalEarnings',
    label: 'Money won',
    detail:
      'Close to noise on its own. Last series’ biggest winner is usually somebody who won one event and was never seen again — weight this lightly.',
  },
  {
    value: 'eventsEntered',
    label: 'Events entered',
    detail:
      'Turning up, cash or no cash. A rostered player who does not enter scores nothing, so how often somebody plays is worth pricing.',
  },
  {
    value: 'bestFinish',
    label: 'Best finish',
    detail:
      'One good result. Favours a player who made a final table over one who min-cashed three times.',
  },
  {
    value: 'firstPlaces',
    label: 'Events won',
    detail:
      'Rare enough that most of the pool sits at nothing, so it separates the very top and does nothing below it.',
  },
];

export const SALARY_SOURCE_LABELS: Record<SalarySource, string> = Object.fromEntries(
  SALARY_SOURCE_CHOICES.map((c) => [c.value, c.label]),
) as Record<SalarySource, string>;

export const ROUNDING_CHOICES: readonly Choice<SalaryRoundingMode>[] = [
  {
    value: 'nearest',
    label: 'Nearest',
    detail: 'Halves round up. What you almost certainly want.',
  },
  {
    value: 'up',
    label: 'Always up',
    detail: 'Every price rounds up. Makes the whole pool slightly dearer, so rosters get tighter.',
  },
  {
    value: 'down',
    label: 'Always down',
    detail:
      'Every price rounds down. Watch the cheapest band — round a price of 3 down to the nearest 5 and it becomes free.',
  },
];

/**
 * A band described by who is in it, not by the numbers on its edges.
 *
 * Bands run weakest-first, so the first is the floor §4.3 is about and the last is the handful of
 * players a roster is built around.
 */
export function describeBand(from: number | null, to: number | null): string {
  if (from === null || to === null) return 'Unfinished band';
  const width = Math.round((to - from) * 10) / 10;

  if (from === 0) return `The weakest ${trimPercent(width)} of the pool`;
  if (to === 100) return `The strongest ${trimPercent(width)}`;
  return `The next ${trimPercent(width)}`;
}

/** The one-line reason the floor band exists, shown against the first band only. */
export const FLOOR_BAND_NOTE =
  'Most of any pool lands here, and that is the model working rather than failing: four cashers in five cashed exactly once, and nothing in the data tells them apart. They are marked unproven, not cheap.';

/**
 * How strong one player is, without printing a number.
 *
 * §9.3 forbids showing a raw percentile and it is right to: the figure is a weighted sum of
 * percentile ranks, which is not a quantity anybody has intuition about. What an operator wants
 * from this column is whether a price looks defensible, which is a five-way answer.
 */
export function strengthLabel(compositePercentile: number | null): string {
  if (compositePercentile === null) return 'No history';
  if (compositePercentile >= 98) return 'Top of the pool';
  if (compositePercentile >= 90) return 'Among the strongest';
  if (compositePercentile >= 75) return 'Stronger than most';
  if (compositePercentile >= 50) return 'Middle of the pool';
  return 'Hard to tell apart';
}

/**
 * §4.5's pool-size rule, as a sentence that says *why*.
 *
 * <b>Both boundaries are real and they fail in opposite directions</b>, so a warning that only
 * said "outside 100–200" would leave an operator unable to tell which way to move. Simulated over
 * the reference data: at 941 draftable players 45% of entrants score zero and the median score is
 * 10; at 16, every team is nearly the same and the contest stops being a contest.
 *
 * Returns null inside the target range — there is nothing to say about a pool that is the right
 * size.
 */
export function poolSizeWarning(poolSize: number): string | null {
  if (poolSize === 0) return null;

  if (poolSize < TARGET_POOL_MINIMUM) {
    return `Only ${poolSize} ${poolSize === 1 ? 'player is' : 'players are'} draftable, below the ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} that works best. With this few to choose from, nearly every entrant builds the same team and the contest comes down to a coin flip. Loosen the minimums, or raise the cap.`;
  }

  if (poolSize > TARGET_POOL_MAXIMUM) {
    return `${poolSize} players are draftable, above the ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} that works best. The wider the pool, the more entrants fill a roster with players who never turn up — at every prior casher, 45% of entrants finished the series on nothing. Tighten the minimums, or lower the cap.`;
  }

  return null;
}

/** The healthy case, said out loud so a correct pool reads as confirmed rather than unremarked. */
export function poolSizeReassurance(poolSize: number): string | null {
  return poolSize >= TARGET_POOL_MINIMUM && poolSize <= TARGET_POOL_MAXIMUM
    ? `${poolSize} draftable players — inside the ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} range that gives entrants real choices without leaving half of them on nothing.`
    : null;
}

/**
 * §4.2.1's second consequence, which is the one nobody expects.
 *
 * Percentile rank uses the midpoint rule, so the best player in a pool of N sits at
 * `100 × (N − ½) / N` and never reaches 100. A narrow top band is therefore **unreachable in a
 * small pool** — §4.2's own 98th-percentile band prices nobody until the pool passes about 25 —
 * and the symptom is a price tier that exists and holds nobody, with nothing on the screen saying
 * why. Warn rather than emit the unused tier.
 */
export function unreachableTopBand(
  topBandFrom: number | null,
  poolSize: number,
): string | null {
  if (topBandFrom === null || poolSize <= 0) return null;

  // The best player's rank. Anything above it cannot be occupied by anybody.
  const best = (100 * (poolSize - 0.5)) / poolSize;
  if (topBandFrom < best) return null;

  const needed = Math.ceil(50 / (100 - topBandFrom));

  return `The top band prices nobody at this pool size. The strongest player in a pool of ${poolSize} cannot reach it — a band starting that high needs about ${needed} players before anybody lands in it. Widen the band, or expect the tier above your second-highest price to sit empty.`;
}

/** "Unsaved changes" versus "unsaved test settings" — the points builder's split, same reasoning. */
export function unsavedSalaryLabel(dirty: { definition: boolean; scope: boolean }): {
  label: string;
  detail: string | null;
} | null {
  if (dirty.definition) return { label: 'Unsaved changes', detail: null };
  if (dirty.scope) {
    return {
      label: 'Unsaved preview settings',
      detail: 'The model itself is unchanged.',
    };
  }
  return null;
}

export const SALARY_PINNED_EXPLANATION =
  'A contest pins the version it publishes with and keeps it. Editing this model later never moves a price anybody has already drafted against.';

/** Trims a trailing `.0`: "50%" rather than "50.0%". */
function trimPercent(value: number): string {
  return `${Number(value.toFixed(1))}%`;
}

/** Money-ish figures in the pool table. Salaries are budget units, not dollars. */
export function formatSalary(value: number): string {
  return Number(value.toFixed(2)).toLocaleString('en-US');
}

export function formatMoney(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

export function formatPoints(value: number): string {
  return Number(value.toFixed(2)).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
